<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="ai-config-modal">
      <div class="modal-header">
        <h2>{{ isEditing ? $t('AI_SETTINGS.EDIT_TITLE') : $t('AI_SETTINGS.ADD_TITLE') }}</h2>
      </div>
      
      <form @submit.prevent="onSubmit" class="modal-form">
        <div class="form-row">
          <label class="form-label">{{ $t('AI_SETTINGS.PROVIDER') }}</label>
          <select v-model="formData.provider" @change="onProviderChange" class="form-select">
            <option value="">{{ $t('AI_SETTINGS.SELECT_PROVIDER') }}</option>
            <option v-for="provider in providers" :key="provider.value" :value="provider.value">
              {{ provider.label }}
            </option>
          </select>
        </div>
        
        <div class="form-row">
          <label class="form-label">{{ $t('AI_SETTINGS.API_KEY') }}</label>
          <input
            v-model="formData.api_key"
            type="password"
            class="form-input"
            :placeholder="$t('AI_SETTINGS.API_KEY_PLACEHOLDER')"
            required
          />
        </div>
        
        <div v-if="showCustomUrl" class="form-row">
          <label class="form-label">{{ $t('AI_SETTINGS.API_URL') }}</label>
          <input
            v-model="formData.api_url"
            type="url"
            class="form-input"
            :placeholder="defaultApiUrl"
          />
        </div>
        
        <div class="form-row">
          <label class="form-label">{{ $t('AI_SETTINGS.MODEL') }}</label>
          <select v-model="formData.model" class="form-select">
            <option v-for="model in availableModels" :key="model" :value="model">
              {{ model }}
            </option>
          </select>
        </div>

        <!-- Bot Configuration Fields -->
        <div class="form-row">
          <label class="form-label">{{ $t('AI_SETTINGS.OVERVIEW.BASIC_INFO.BOT_NAME') }}</label>
          <input
            v-model="formData.bot_name"
            type="text"
            class="form-input"
            :placeholder="$t('AI_SETTINGS.OVERVIEW.BASIC_INFO.BOT_NAME_PLACEHOLDER')"
            required
          />
        </div>

        <div class="form-row">
          <label class="form-label">{{ $t('AI_SETTINGS.OVERVIEW.BASIC_INFO.BUSINESS_NAME') }}</label>
          <input
            v-model="formData.business_name"
            type="text"
            class="form-input"
            :placeholder="$t('AI_SETTINGS.OVERVIEW.BASIC_INFO.BUSINESS_NAME_PLACEHOLDER')"
            required
          />
        </div>


        <div class="form-row">
          <label class="form-label">{{ $t('AI_SETTINGS.OVERVIEW.BASIC_INFO.LANGUAGE') }}</label>
          <select v-model="formData.language" class="form-select">
            <option value="auto">{{ $t('AI_SETTINGS.OVERVIEW.BASIC_INFO.LANGUAGE_OPTIONS.auto') }}</option>
            <option value="en">{{ $t('AI_SETTINGS.OVERVIEW.BASIC_INFO.LANGUAGE_OPTIONS.en') }}</option>
            <option value="vi">{{ $t('AI_SETTINGS.OVERVIEW.BASIC_INFO.LANGUAGE_OPTIONS.vi') }}</option>
          </select>
        </div>
        
        <div class="modal-footer">
          <woot-button
            type="button"
            variant="clear"
            @click="onClose"
          >
            {{ $t('AI_SETTINGS.CANCEL') }}
          </woot-button>
          
          <woot-button
            type="submit"
            color-scheme="primary"
            :loading="isSaving"
          >
            {{ isEditing ? $t('AI_SETTINGS.UPDATE') : $t('AI_SETTINGS.CREATE') }}
          </woot-button>
        </div>
      </form>
    </div>
  </woot-modal>
</template>

<script>
export default {
  name: 'AiConfigurationModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    configuration: {
      type: Object,
      default: null,
    },
    providers: {
      type: Array,
      default: () => [],
    },
  },
  
  data() {
    return {
      formData: {
        provider: '',
        api_key: '',
        api_url: '',
        model: 'gpt-3.5-turbo',
        // Bot configuration fields
        bot_name: '',
        business_name: '',
        language: 'auto',
        response_speed: 5,
        temperature: 0.6,
        addressing_style: { xung: 'mình', ho: 'bạn' },
        unknown_question_action: 'auto_reply',
        bot_silent: false,
        bot_version: '2.0',
        product_consulting_enabled: false,
        customer_support_enabled: false,
      },
      availableModels: ['gpt-3.5-turbo'],
      isSaving: false,
    }
  },
  
  computed: {
    isEditing() {
      return !!this.configuration
    },
    
    showCustomUrl() {
      return this.formData.provider === 'custom' || this.formData.api_url
    },
    
    defaultApiUrl() {
      const urls = {
        openai: 'https://api.openai.com/v1/chat/completions',
        claude: 'https://api.anthropic.com/v1/messages',
        gemini: 'https://generativelanguage.googleapis.com/v1/models',
        custom: 'https://your-api-endpoint.com/v1/chat/completions',
      }
      return urls[this.formData.provider] || ''
    },
  },
  
  watch: {
    configuration: {
      immediate: true,
      handler(config) {
        if (config) {
          this.formData = {
            provider: config.provider,
            api_key: '', // Don't pre-fill for security
            api_url: config.api_url || '',
            model: config.model,
            // Bot configuration fields
            bot_name: config.bot_name || '',
            business_name: config.business_name || '',
            language: config.language || 'auto',
            response_speed: config.response_speed || 5,
            temperature: config.temperature || 0.6,
            addressing_style: config.addressing_style || { xung: 'mình', ho: 'bạn' },
            unknown_question_action: config.unknown_question_action || 'auto_reply',
            bot_silent: config.bot_silent || false,
            bot_version: config.bot_version || '2.0',
            product_consulting_enabled: config.product_consulting_enabled || false,
            customer_support_enabled: config.customer_support_enabled || false,
          }
          this.loadModelsForProvider(config.provider)
        } else {
          this.resetForm()
        }
      },
    },
  },
  
  methods: {
    onClose() {
      this.$emit('close')
      this.resetForm()
    },
    
    async onSubmit() {
      this.isSaving = true
      try {
        const submitData = { ...this.formData }
        
        // Set default API URL if not provided
        if (!submitData.api_url && this.defaultApiUrl) {
          submitData.api_url = this.defaultApiUrl
        }
        
        this.$emit('save', submitData)
      } catch (error) {
        console.error('Error saving configuration:', error)
      } finally {
        this.isSaving = false
      }
    },
    
    async onProviderChange() {
      await this.loadModelsForProvider(this.formData.provider)
      
      // Set default model
      if (this.availableModels.length > 0) {
        this.formData.model = this.availableModels[0]
      }
      
      // Set default API URL
      if (this.defaultApiUrl && !this.formData.api_url) {
        this.formData.api_url = this.defaultApiUrl
      }
    },
    
    async loadModelsForProvider(provider) {
      if (!provider) {
        this.availableModels = []
        return
      }
      
      try {
                 const response = await window.axios.get(
           `/api/v1/accounts/${this.$store.getters.getCurrentAccount.id}/ai_configurations/models_for_provider?provider=${provider}`
         )
        this.availableModels = response.data.models || []
      } catch (error) {
        console.error('Error loading models:', error)
        this.availableModels = ['gpt-3.5-turbo'] // Fallback
      }
    },
    
    resetForm() {
      this.formData = {
        provider: '',
        api_key: '',
        api_url: '',
        model: 'gpt-3.5-turbo',
        // Bot configuration fields
        bot_name: '',
        business_name: '',
        language: 'auto',
        response_speed: 5,
        temperature: 0.6,
        addressing_style: { xung: 'mình', ho: 'bạn' },
        unknown_question_action: 'auto_reply',
        bot_silent: false,
        bot_version: '2.0',
        product_consulting_enabled: false,
        customer_support_enabled: false,
      }
      this.availableModels = ['gpt-3.5-turbo']
      this.isSaving = false
    },
  },
}
</script>

<style lang="scss" scoped>
.ai-config-modal {
  width: 100%;
  max-width: 600px;
}

.modal-header {
  margin-bottom: var(--space-large);
  
  h2 {
    margin: 0;
    color: var(--color-heading);
    font-size: var(--font-size-large);
  }
}

.modal-form {
  .form-row {
    margin-bottom: var(--space-normal);
    
    .form-label {
      display: block;
      margin-bottom: var(--space-smaller);
      font-weight: var(--font-weight-medium);
      color: var(--color-heading);
    }
    
    .form-input,
    .form-select {
      width: 100%;
      padding: var(--space-small);
      border: 1px solid var(--color-border);
      border-radius: var(--border-radius-normal);
      font-size: var(--font-size-default);
      
      &:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: 0 0 0 1px var(--color-primary);
      }
    }
    
    .form-checkbox {
      display: flex;
      align-items: center;
      cursor: pointer;
      
      input[type="checkbox"] {
        margin-right: var(--space-small);
      }
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-small);
  margin-top: var(--space-large);
  padding-top: var(--space-normal);
  border-top: 1px solid var(--color-border-light);
}

@media (max-width: 768px) {
  .ai-config-modal {
    max-width: 100%;
    margin: var(--space-small);
  }
  
  .modal-footer {
    flex-direction: column-reverse;
    
    .woot-button {
      width: 100%;
    }
  }
}
</style> 